<template>
  <div v-click-outside="hide">
    <label class="block text-sm font-medium text-gray-700" v-if="showLabel">
      {{ label }}
    </label>

    <input type="text"
           :placeholder="placeholder"
           autocomplete="off"
           class="mt-1 block w-full pr-10 border-gray-300 text-gray-900 placeholder-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md rounded-md shadow-sm"
           v-model="query"
           @focus="showOnFocus"
           />

    <div class="relative z-50" v-if="show">
      <div class="absolute top-100 mt-1 w-full border bg-white shadow-xl rounded">
        <div class="p-3 max-h-72 overflow-y-auto">
          <div class="divide-y" x-ref="list">
            <p v-if="loading" class="p-2 flex block w-full rounded hover:bg-gray-100">
              Loading ...
            </p>
            <p v-if="! loading && ! searchResults.length" class="p-2 flex block w-full rounded hover:bg-gray-100">
              No results
            </p>
            <button v-for="(suggestion, index) in searchResults"
                    :key="index"
                    type="button"
                    class="p-2 flex block w-full rounded hover:bg-gray-100 text-left"
                    @click="selectItem(suggestion)">
              <span>{{ suggestion.name }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <slot name="selected-items" v-if="! single">
      <div class="mt-4 flex gap-2 flex-wrap">
        <Badge v-for="item in selectedItems"
               :key="item.id"
               class="">
        {{ item.name }} <XIcon class="ml-1 w-3 h-3 cursor-pointer text-gray-400 hover:text-gray-700" @click="removeItem(item.id)" />
        </Badge>
      </div>
    </slot>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import Axios from 'axios'
import Badge from '@/components/badge'
import { XIcon } from '@heroicons/vue/solid'

export default {
  components: {
    Badge,
    XIcon,
  },

  props: {
    selected: {
      type: [Array, String],
      default: () => ([]),
    },
    label: {
      type: String,
    },
    placeholder: {
      type: String,
      required: false,
    },
    url: String,
    options: {
      type: Array,
      default: [],
    },
    static: {
      type: Boolean,
      default: false,
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
    single: {
      type: Boolean,
      default: false,
    },
    clearOnBlur: {
      type: Boolean,
      default: false,
    },
    searchOnFocus: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'change',
  ],

  data() {
    return {
      show: false,
      query: this.single
        ? this.selected
        : null,
      loading: false,
      items: this.options,
      selectedItems: this.single
        ? [{ id: this.selected, value: this.selected }]
        : [...this.selected],
    }
  },

  methods: {
    async startSearch() {
      this.show = true

      if (this.static) {
        this.items = this.options.filter(({ name }) => new RegExp(this.query || '', 'i').test(name))
        return
      }

      this.items = []
      this.loading = true

      const results = await Axios.get(this.url, {
        params: {
          query: this.query,
        },
      })

      const { data } = results.data

      this.items = data
      this.loading = false
    },

    showOnFocus() {
      if (this.searchOnFocus) {
        this.startSearch();
        return;
      }

      if (this.items.length) {
        this.show = true
      }
    },

    selectItem(item) {
      if (this.single) {
        return this.selectSingle(item)
      }

      const foundIndex = this.selectedItems.findIndex(({ id }) => id === item.id)

      if (foundIndex < 0) {
        this.selectedItems.push(item)
        this.$emit('change', this.selectedItems.map(({ id }) => id))
      }

      this.show = false
    },

    selectSingle(item) {
      this.selectedItems = [item]
      this.$emit('change', item.id)

      this.query = item.name
      this.items = []

      nextTick(() => {
        this.show = false
      })
    },

    hide() {
      this.show = false

      if (this.clearOnBlur) {
        this.items = []
      }
    },

    removeItem(itemId) {
      this.selectedItems = this.selectedItems.filter(({ id }) => id !== itemId)
      this.$emit('change', this.selectedItems.map(({ id }) => id))
    },
  },

  watch: {
    query(newValue) {
      this.startSearch()
    },
  },

  computed: {
    searchResults() {
      if (this.single) {
        return this.items
      }

      const selected = this.selectedItems.map(({ id }) => id)

      return this.items.filter(item => ! selected.includes(item.id))
    },
  },
}
</script>
